import React, { useRef, useState } from 'react';
import { otherLabel, otherValueOf } from '@/utils/programForm';

// Option list for `single_choice` (radios, answer = string) and `multi_choice`
// (checkboxes, answer = array of strings), plus an optional "Other" row
// (question.allow_other) with a free-text box. The typed text IS the answer —
// picking "Other" contributes nothing until something is typed, so a required
// question still blocks submission on an empty Other.
const ChoiceField = ({ q, value, onChange, testId }) => {
  const options = q.options || [];
  const multi = q.type === 'multi_choice';
  // Selected options, minus the free-text entry (tracked separately below).
  const otherText = otherValueOf(q, value);
  const selected = multi
    ? (Array.isArray(value) ? value : (value ? [value] : [])).filter((v) => options.includes(v))
    : (typeof value === 'string' && options.includes(value) ? [value] : []);

  // Keep "Other" ticked while its box is still empty (nothing to derive from).
  const [otherPicked, setOtherPicked] = useState(() => otherText !== '');
  const otherRef = useRef(null);
  const otherChecked = !!q.allow_other && (otherPicked || otherText !== '');

  // Rebuild a multi answer in option order, with the free text last.
  const emitMulti = (picked, text) => onChange([...options.filter((o) => picked.includes(o)), ...(text ? [text] : [])]);

  const pickOption = (opt) => {
    if (!multi) { setOtherPicked(false); onChange(opt); return; }
    const next = selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt];
    emitMulti(next, otherChecked ? otherText : '');
  };
  const pickOther = () => {
    if (multi) {
      const on = !otherChecked;
      setOtherPicked(on);
      emitMulti(selected, on ? otherText : '');
      if (on) otherRef.current?.focus();
      return;
    }
    setOtherPicked(true);
    onChange('');            // radios are exclusive — drop the previously chosen option
    otherRef.current?.focus();
  };
  const typeOther = (text) => {
    setOtherPicked(true);
    if (multi) emitMulti(selected, text);
    else onChange(text);
  };

  const rowClass = 'flex items-center gap-2 cursor-pointer p-2 bg-background border border-border rounded-lg hover:border-primary/40';
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {options.map((opt) => (
        <label key={opt} className={rowClass}>
          <input
            type={multi ? 'checkbox' : 'radio'}
            name={q.id}
            value={opt}
            checked={multi ? selected.includes(opt) : (!otherChecked && selected.includes(opt))}
            onChange={() => pickOption(opt)}
          />
          <span className="text-sm text-text-primary">{opt}</span>
        </label>
      ))}
      {q.allow_other && (
        <label className={rowClass}>
          <input
            type={multi ? 'checkbox' : 'radio'}
            name={q.id}
            checked={otherChecked}
            onChange={pickOther}
            data-testid={`q-other-radio-${q.id}`}
          />
          <span className="text-sm text-text-primary shrink-0">{otherLabel(q)}:</span>
          <input
            ref={otherRef}
            type="text"
            value={otherChecked ? otherText : ''}
            maxLength={500}
            placeholder="Type your answer"
            onFocus={() => { if (!otherChecked) pickOther(); }}
            onChange={(e) => typeOther(e.target.value)}
            data-testid={`q-other-input-${q.id}`}
            className="flex-1 min-w-0 bg-transparent border-b border-border focus:border-primary px-1 py-0.5 text-sm text-text-primary focus:outline-none transition-colors"
          />
        </label>
      )}
    </div>
  );
};

export default ChoiceField;
